import React, { useEffect, useState, useCallback } from "react";
import Index from "../../../Index";
import PageIndex from "../../../PageIndex";
import CommonPagination from "../../../../component/common/PaginationMaster";
import DateRangePickerMaster from "../../../../component/common/DateRangePickerMaster";
import SearchMaster from "../../../../component/common/SearchMaster";
import ExcelExportHelper from "../../../../component/common/ExcelExportHelper";

function WalletRegisterImportActivity() {
  const [loading, setLoading] = useState(false);
  const [walletActivityData, setWalletActivityData] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  // State for Search and date filter
  const [selectedDate, setSelectedDate] = useState([null, null]);
  const [searchValue, setSearchValue] = useState("");

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecordCount, setTotalRecordCount] = useState(0);
  const [paginationPerPage, setPaginationPerPage] = useState(10);
  const [pageDefault, setPageDefault] = useState(null);

  // Get list of WalletActivity
  const getWalletCreatingActivityList = useCallback(async () => {
    setLoading(true);
    try {
      let walletBaseUrl = `${PageIndex.Api.GET_WALLET_CREATING_ACTIVITY_LIST}`;

      const newParams = new URLSearchParams();
      newParams.append(
        "page",
        pageDefault !== null ? String(pageDefault) : String(currentPage)
      );
      newParams.append("size", String(paginationPerPage));

      if (searchValue) {
        newParams.append("search", searchValue);
      }
      if (selectedDate && selectedDate[0] && selectedDate[1]) {
        newParams.append("from", selectedDate[0].format("YYYY-MM-DD"));
        newParams.append("to", selectedDate[1].format("YYYY-MM-DD"));
      }

      const queryString = newParams.toString();
      if (queryString) {
        walletBaseUrl += `?${queryString}`;
      }

      const res = await PageIndex.doGet(walletBaseUrl);

      if (res?.status === 200 && res?.data?.data) {
        const walletActivityModify = res.data.data.map((item) => ({
          _id: item?._id,
          walletAction: item.walletAction?.toUpperCase() || "-",
          appType:
            item.appType === 0 ? "Android" : item.appType === 1 ? "IOS" : "-",
          walletAddress: item.walletAddress || "-",
          createdAt: item.createdAt
            ? Index.moment(item.createdAt)
                .utcOffset("+05:30")
                .format("DD-MM-YYYY hh:mm A")
            : "-",
        }));
        setCurrentPage(res.data.currentPage || 1);
        setTotalRecordCount(res.data.totalWalletActivityLogs || 0);
        setWalletActivityData(walletActivityModify);
      } else {
        setWalletActivityData([]);
        setTotalRecordCount(0);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Error fetching wallet activity list:", error);
      setWalletActivityData([]);
      setTotalRecordCount(0);
      setCurrentPage(1);
    } finally {
      if (pageDefault !== null) {
        setPageDefault(null);
      }
      setLoading(false);
    }
  }, [currentPage, paginationPerPage, searchValue, selectedDate, pageDefault]);

  useEffect(() => {
    getWalletCreatingActivityList();
  }, [getWalletCreatingActivityList]);

  // Get list of Download csv
  const handleDownloadCsv = async () => {
    if (isExporting) return;
    setIsExporting(true);
    setExportData([]);

    try {
      let exportUrl = `${PageIndex.Api.GET_EXPORT_CSV}`;
      const newParams = new URLSearchParams();

      if (searchValue) {
        newParams.append("search", searchValue);
      }
      if (selectedDate && selectedDate[0] && selectedDate[1]) {
        newParams.append("from", selectedDate[0].format("YYYY-MM-DD"));
        newParams.append("to", selectedDate[1].format("YYYY-MM-DD"));
      }

      const queryString = newParams.toString();
      if (queryString) {
        exportUrl += `?${queryString}`;
      }

      const res = await PageIndex.doGet(exportUrl);

      if (res?.status === 200 && res?.data?.data) {
        const transformedData = res.data.data.map((item) => ({
          WALLETADDRESS: item.walletAddress || "-",
          APPTYPE:
            item.appType === 0 ? "Android" : item.appType === 1 ? "IOS" : "-",
          WALLETACTION: item.walletAction?.toUpperCase() || "-",
          DATE: item.createdAt
            ? Index.moment(item.createdAt)
                .utcOffset("+05:30")
                .format("DD-MM-YYYY hh:mm A")
            : "-",
        }));
        setExportData(transformedData);
      } else {
        console.error(
          "Failed to fetch data for export or no data received:",
          res
        );
      }
    } catch (error) {
      console.error("Error downloading CSV data:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const shortcutsItems = [{ label: "Reset", getValue: () => [null, null] }];

  return (
    <>
      <Index.DynamicTitle title="presale-wraper" />
      <Index.Box className="presale-wraper">
        <Index.Box className="res-cus-table">
          <Index.Box className="offering-cus-table">
            <Index.Box className="offering-table-head tryed">
              <Index.Box className="title-main">
                <Index.Typography
                  variant="p"
                  component="p"
                  className="page-title"
                >
                  Wallet Creating Activity Logs
                </Index.Typography>
              </Index.Box>

              <Index.Box className="date-search-wrapper">
                <DateRangePickerMaster
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  shortcutsItems={shortcutsItems}
                />
                <SearchMaster setSearchValue={setSearchValue} />

                <Index.Box className="common-button grey-button">
                  <Index.Button
                    variant="contained"
                    onClick={handleDownloadCsv}
                    disabled={isExporting || walletActivityData.length === 0}
                  >
                    {isExporting ? "Preparing Export..." : "Export to Excel"}
                  </Index.Button>
                </Index.Box>

                {/* ExcelExportHelper will trigger download when exportData is populated */}
                {exportData.length > 0 && !isExporting && (
                  <ExcelExportHelper
                    data={exportData}
                    sheetName={"Wallet_Register_Activity_Logs"}
                    fileName={"WalletRegisterActivityLogs"}
                    setExportData={setExportData}
                  />
                )}
              </Index.Box>
            </Index.Box>
          </Index.Box>
          <Index.Box className="border-round-table">
            <Index.TableContainer
              component={Index.Paper}
              className="table-container"
            >
              <Index.Table
                aria-label="wallet activity logs table"
                className="table-design-main supply-table one-line-table invoice-table-set user-management-table trader-list"
              >
                <Index.TableHead className="gradient-bg">
                  <Index.TableRow>
                    <Index.TableCell align="left">
                      Wallet Address
                    </Index.TableCell>
                    <Index.TableCell align="left">App Type</Index.TableCell>
                    <Index.TableCell align="left">
                      Wallet Action
                    </Index.TableCell>
                    <Index.TableCell align="left">Date (IST)</Index.TableCell>
                  </Index.TableRow>
                </Index.TableHead>
                <Index.TableBody>
                  {loading ? (
                    <Index.TableRow>
                      <Index.TableCell colSpan={4} align="center">
                        <Index.Loading />
                      </Index.TableCell>
                    </Index.TableRow>
                  ) : walletActivityData.length > 0 ? (
                    walletActivityData.map((item) => (
                      <Index.TableRow key={item._id}>
                        <Index.TableCell>{item.walletAddress}</Index.TableCell>
                        <Index.TableCell>{item.appType}</Index.TableCell>
                        <Index.TableCell>{item.walletAction}</Index.TableCell>
                        <Index.TableCell>{item.createdAt}</Index.TableCell>
                      </Index.TableRow>
                    ))
                  ) : (
                    <Index.TableRow>
                      <Index.TableCell colSpan={4} align="center">
                        <Index.NoDataFound message="No wallet activity logs found." />
                      </Index.TableCell>
                    </Index.TableRow>
                  )}
                </Index.TableBody>
              </Index.Table>
            </Index.TableContainer>
          </Index.Box>
          {totalRecordCount > 0 && !loading && (
            <CommonPagination
              paginationPerPage={paginationPerPage}
              setPaginationPerPage={setPaginationPerPage}
              setPageDefault={setPageDefault} // Allows forcing a page, then resets
              totalRecordCount={totalRecordCount}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          )}
        </Index.Box>
      </Index.Box>
    </>
  );
}

export default WalletRegisterImportActivity;
